
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { ChefHat, Lightbulb, Utensils, Info } from "lucide-react";
import { useUrlValidation } from "@/hooks/useUrlValidation";

export const RecipeInput = ({ onSubmit }: { onSubmit: (url: string) => void }) => {
  const [url, setUrl] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { validateUrl, resetValidationState } = useUrlValidation();

  // Reset validation state when component mounts or unmounts
  useEffect(() => {
    resetValidationState();
    return () => resetValidationState();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateUrl(url)) {
      onSubmit(url);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-2xl mx-auto space-y-4"
    >
      <div className="flex items-center justify-center gap-2">
        <motion.div
          animate={{ 
            rotate: isHovered ? [0, -10, 10, -10, 0] : 0,
            scale: isHovered ? 1.1 : 1
          }}
          transition={{ duration: 0.5 }}
        >
          <ChefHat className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
        </motion.div>
        <motion.div
          animate={{ 
            rotate: isHovered ? [0, 10, -10, 10, 0] : 0,
            scale: isHovered ? 1.1 : 1
          }}
          transition={{ duration: 0.5 }}
        >
          <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
        </motion.div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 px-4 sm:px-0">
        <motion.div 
          className="relative"
          animate={{ 
            scale: isFocused ? 1.02 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <Input
            type="url"
            placeholder="Paste your cooking or baking recipe link here!"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="w-full px-4 py-3 text-base rounded-xl border-2 border-primary/20 focus:border-primary shadow-sm focus:shadow-md transition-all duration-300"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/40">
            <Lightbulb size={20} />
          </div>
        </motion.div>
        
        <div className="bg-blue-50 p-3 rounded-md flex items-start">
          <Info className="text-blue-500 mt-0.5 mr-2 h-4 w-4 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Works best with popular recipe sites like Simply Recipes, King Arthur Baking, Bon Appetit, Serious Eats, and more. Some sites like AllRecipes may block scraping.
          </p>
        </div>
        
        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
        >
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-primary to-primary-foreground/90 hover:opacity-90 text-white px-6 py-3 rounded-xl transition-all duration-300 text-lg font-medium shadow-md hover:shadow-lg"
          >
            Make it Kid-Friendly! 🧑‍🍳✨
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
};
